// Cardiovascular Medications Reference Data
// Comprehensive drug information for anesthesia practice

export const cvMedications = [
  // ========== VASOPRESSORS ==========
  {
    name: 'Phenylephrine',
    proprietaryName: 'Neo-Synephrine',
    class: 'vasopressor',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'α1': '+++',
      'β1': '-',
      'β2': '-'
    },
    hemodynamics: {
      HR: '↓',
      contractility: '↔',
      SVR: '↑↑↑',
      PVR: '↑',
      preload: '↔'
    },
    dosing: {
      infusion: '40-200 mcg/min (0.5-3 mcg/kg/min)',
      bolus: '50-200 mcg IV push',
      units: 'mcg/min'
    },
    concentration: {
      standard: '400 mcg/mL (40 mg in 100 mL)',
      bolus: '100 mcg/mL (10 mg in 100 mL)'
    },
    onset: '1-2 minutes',
    duration: '5-10 minutes',
    metabolism: 'Hepatic (MAO, sulfotransferase)',
    uses: [
      'First-line for pure vasodilation (e.g., spinal hypotension)',
      'Hypotension with tachycardia',
      'SVT treatment (vagal maneuver)'
    ],
    considerations: [
      'Pure α-agonist - no cardiac effects',
      'Reflex bradycardia common',
      'Can decrease cardiac output in hypovolemia',
      'Safe in pregnancy',
      'Extravasation risk - use central line if possible'
    ],
    bestFor: 'Pure ↑SVR without cardiac stimulation'
  },
  {
    name: 'Norepinephrine',
    proprietaryName: 'Levophed',
    class: 'vasopressor',
    categories: ['cardiovascular', 'emergency'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'α1': '+++',
      'β1': '++',
      'β2': '-'
    },
    hemodynamics: {
      HR: '↑/↔',
      contractility: '↑↑',
      SVR: '↑↑↑',
      PVR: '↑',
      preload: '↔'
    },
    dosing: {
      infusion: '2-20 mcg/min (0.01-0.3 mcg/kg/min)',
      units: 'mcg/min'
    },
    concentration: {
      standard: '16 mcg/mL (4 mg in 250 mL)',
      alternative: '32 mcg/mL (8 mg in 250 mL)'
    },
    onset: '1-2 minutes',
    duration: '1-2 minutes after D/C',
    metabolism: 'MAO and COMT',
    uses: [
      'Septic shock (first-line vasopressor)',
      'Cardiogenic shock',
      'Hypotension with low SVR'
    ],
    considerations: [
      'Balanced α and β effects',
      'Less tachycardia than epinephrine',
      'Risk of arrhythmias at high doses',
      'Extravasation can cause tissue necrosis',
      'Requires central access for prolonged use'
    ],
    bestFor: '↑SVR + inotropy'
  },
  {
    name: 'Epinephrine',
    proprietaryName: 'Adrenalin',
    class: 'vasopressor',
    categories: ['cardiovascular', 'emergency', 'regional'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'α1': '+++',
      'β1': '+++',
      'β2': '++'
    },
    hemodynamics: {
      HR: '↑↑',
      contractility: '↑↑↑',
      SVR: '↑ (dose-dependent)',
      PVR: '↓',
      preload: '↔'
    },
    dosing: {
      infusion: '1-10 mcg/min (0.01-0.1 mcg/kg/min)',
      bolus: '10-100 mcg IV (0.1-1 mL of 1:10,000)',
      units: 'mcg/min'
    },
    concentration: {
      standard: '16 mcg/mL (4 mg in 250 mL)',
      push: '10 mcg/mL (1 mg in 100 mL - 1:100,000)',
      code: '100 mcg/mL (1 mg in 10 mL - 1:10,000)'
    },
    onset: 'IV: 1-2 min',
    duration: '5-10 minutes',
    metabolism: 'MAO and COMT',
    uses: [
      'Cardiac arrest (1 mg IV q3-5min)',
      'Anaphylaxis (0.3-0.5 mg IM)',
      'Severe bradycardia',
      'Refractory hypotension'
    ],
    considerations: [
      'Most potent inotrope and chronotrope',
      'β effects dominate at low doses, α at high doses',
      'Can cause severe tachycardia and arrhythmias',
      'Increases myocardial O2 consumption',
      'Hyperglycemia (β2 effect)',
      'For regional: prolongs block, reduces systemic absorption'
    ],
    bestFor: 'Severe hypotension or code',
    contexts: {
      cardiovascular: {
        subtitle: 'Pressor/Inotrope for cardiac support',
        dosing: '1-10 mcg/min infusion',
        uses: 'Septic shock, cardiac arrest, severe hypotension'
      },
      emergency: {
        subtitle: 'First-line for cardiac arrest',
        dosing: '1mg IV push q3-5min',
        uses: 'VF, pulseless VT, asystole, PEA, anaphylaxis'
      },
      regional: {
        subtitle: 'Additive to local anesthetics',
        dosing: '1:200,000 concentration (5 mcg/mL)',
        uses: 'Prolongs block, reduces systemic absorption'
      }
    }
  },
  {
    name: 'Vasopressin',
    proprietaryName: 'Pitressin',
    class: 'vasopressor',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'V1': '+++',
      'α1': '-',
      'β1': '-'
    },
    hemodynamics: {
      HR: '↔',
      contractility: '↔',
      SVR: '↑↑↑',
      PVR: '↑↑',
      preload: '↔'
    },
    dosing: {
      infusion: '0.01-0.04 units/min',
      bolus: '0.5-2 units IV (for cardiac arrest)',
      units: 'units/min'
    },
    concentration: {
      standard: '0.4 units/mL (20 units in 50 mL or 100 units in 250 mL)'
    },
    onset: '5-10 minutes',
    duration: '10-20 minutes',
    metabolism: 'Hepatic and renal',
    uses: [
      'Catecholamine-refractory septic shock',
      'Vasodilatory shock (post-CPB)',
      'Diabetes insipidus',
      'GI bleeding (off-label)'
    ],
    considerations: [
      'Non-adrenergic vasoconstriction',
      'Does NOT increase HR or inotropy',
      'Can cause coronary vasoconstriction',
      'May decrease cardiac output in some patients',
      'Useful when catecholamine receptors downregulated',
      'Potent pulmonary vasoconstrictor'
    ],
    bestFor: 'Refractory vasodilatory shock'
  },
  {
    name: 'Dopamine',
    proprietaryName: 'Intropin',
    class: 'vasopressor',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'D1': '+ (low dose)',
      'β1': '++ (moderate dose)',
      'α1': '+++ (high dose)'
    },
    hemodynamics: {
      HR: '↑ (dose-dependent)',
      contractility: '↑ (dose-dependent)',
      SVR: '↑ (dose-dependent)',
      PVR: '↔/↑',
      preload: '↔'
    },
    dosing: {
      infusion: '2-20 mcg/kg/min',
      units: 'mcg/kg/min',
      ranges: {
        low: '2-5 mcg/kg/min (dopaminergic)',
        moderate: '5-10 mcg/kg/min (β1)',
        high: '>10 mcg/kg/min (α1)'
      }
    },
    concentration: {
      standard: '1600 mcg/mL (400 mg in 250 mL)',
      alternative: '3200 mcg/mL (800 mg in 250 mL)'
    },
    onset: '2-5 minutes',
    duration: '10 minutes',
    metabolism: 'MAO, COMT, renal',
    uses: [
      'Bradycardia with hypotension',
      'Low cardiac output states',
      'Formerly used for "renal protection" (NOT evidence-based)'
    ],
    considerations: [
      'Dose-dependent receptor activation',
      'Low doses: renal vasodilation (NO longer recommended for renal protection)',
      'Moderate doses: inotropy and chronotropy',
      'High doses: vasoconstriction (similar to norepinephrine)',
      'More arrhythmogenic than norepinephrine',
      'Generally replaced by more selective agents',
      'Contraindicated with MAOIs'
    ],
    bestFor: 'Less commonly used; mostly replaced by other agents'
  },

  // ========== INOTROPES ==========
  {
    name: 'Dobutamine',
    proprietaryName: 'Dobutrex',
    class: 'inotrope',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'β1': '+++',
      'β2': '++',
      'α1': '+'
    },
    hemodynamics: {
      HR: '↑',
      contractility: '↑↑↑',
      SVR: '↓',
      PVR: '↓',
      preload: '↓'
    },
    dosing: {
      infusion: '2-20 mcg/kg/min',
      units: 'mcg/kg/min'
    },
    concentration: {
      standard: '1000 mcg/mL (250 mg in 250 mL)',
      alternative: '2000 mcg/mL (500 mg in 250 mL)'
    },
    onset: '1-2 minutes',
    duration: '10 minutes',
    metabolism: 'Hepatic (COMT)',
    uses: [
      'Cardiogenic shock',
      'Low cardiac output after cardiac surgery',
      'Heart failure exacerbation',
      'Pharmacologic stress testing'
    ],
    considerations: [
      'Pure inotrope with minimal vasopressor effect',
      'Can worsen hypotension (due to SVR decrease)',
      'Often combined with vasopressor',
      'Tachycardia and arrhythmias common',
      'Increases myocardial O2 demand',
      'May need to add phenylephrine if BP drops'
    ],
    bestFor: 'Low cardiac output with adequate SVR'
  },
  {
    name: 'Milrinone',
    proprietaryName: 'Primacor',
    class: 'inotrope',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'PDE3 inhibitor': '+++'
    },
    hemodynamics: {
      HR: '↑',
      contractility: '↑↑',
      SVR: '↓↓',
      PVR: '↓↓',
      preload: '↓'
    },
    dosing: {
      loading: '50 mcg/kg over 10 min (optional)',
      infusion: '0.25-0.75 mcg/kg/min',
      units: 'mcg/kg/min'
    },
    concentration: {
      standard: '200 mcg/mL (20 mg in 100 mL or 40 mg in 200 mL)'
    },
    onset: '5-15 minutes',
    duration: '1-2 hours (long half-life)',
    metabolism: 'Renal (80% unchanged)',
    uses: [
      'Right ventricular failure',
      'Pulmonary hypertension',
      'Post-CPB low cardiac output',
      'Bridge to heart transplant',
      'Acute decompensated heart failure'
    ],
    considerations: [
      'Inotrope AND vasodilator (inodilator)',
      'Excellent for RV dysfunction with pulmonary HTN',
      'Almost always need to add vasopressor',
      'Long half-life - difficult to titrate',
      'Thrombocytopenia with prolonged use',
      'Reduce dose in renal failure',
      'Does NOT increase O2 consumption (unlike catecholamines)'
    ],
    bestFor: 'RV dysfunction or pulmonary hypertension'
  },

  // ========== VASODILATORS ==========
  {
    name: 'Nitroglycerin',
    proprietaryName: 'Tridil',
    class: 'vasodilator',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'Nitric oxide donor': '+++'
    },
    hemodynamics: {
      HR: '↑ (reflex)',
      contractility: '↓ (indirect)',
      SVR: '↓',
      PVR: '↓',
      preload: '↓↓↓'
    },
    dosing: {
      infusion: '5-200 mcg/min',
      units: 'mcg/min'
    },
    concentration: {
      standard: '100 mcg/mL (50 mg in 500 mL or 25 mg in 250 mL)',
      alternative: '200 mcg/mL (50 mg in 250 mL)'
    },
    onset: '1-2 minutes',
    duration: '5-10 minutes',
    metabolism: 'Hepatic',
    uses: [
      'Acute coronary syndrome',
      'Hypertensive emergency',
      'Heart failure (reduce preload)',
      'Controlled hypotension'
    ],
    considerations: [
      'Primarily venodilator (preload reduction)',
      'Antianginal - decreases myocardial O2 demand',
      'Tolerance develops with prolonged use',
      'Can cause severe headache',
      'Contraindicated with PDE5 inhibitors (sildenafil)',
      'Methemoglobinemia with high doses',
      'Requires special non-PVC tubing (some formulations)'
    ],
    bestFor: 'Preload reduction or angina'
  },
  {
    name: 'Nicardipine',
    proprietaryName: 'Cardene',
    class: 'vasodilator',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'Calcium channel blocker': '+++'
    },
    hemodynamics: {
      HR: '↑ (reflex)',
      contractility: '↔',
      SVR: '↓↓',
      PVR: '↓',
      preload: '↔'
    },
    dosing: {
      infusion: '5-15 mg/hr (initial: 5 mg/hr, titrate by 2.5 mg/hr q5-15min)',
      units: 'mg/hr'
    },
    concentration: {
      standard: '0.1 mg/mL (25 mg in 250 mL)',
      premixed: '0.1 mg/mL or 0.2 mg/mL'
    },
    onset: '5-15 minutes',
    duration: '30-40 minutes',
    metabolism: 'Hepatic (CYP3A4)',
    uses: [
      'Hypertensive emergency',
      'Controlled hypotension',
      'Post-cardiac surgery HTN',
      'Acute ischemic stroke (permissive HTN)'
    ],
    considerations: [
      'Selective arteriodilator',
      'Minimal negative inotropy (vs diltiazem)',
      'Reflex tachycardia common',
      'Preferred over nitroprusside (no cyanide risk)',
      'Can increase ICP',
      'Rebound HTN if stopped abruptly'
    ],
    bestFor: 'Controlled BP reduction'
  },
  {
    name: 'Sodium Nitroprusside',
    proprietaryName: 'Nipride',
    class: 'vasodilator',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'Nitric oxide donor': '+++'
    },
    hemodynamics: {
      HR: '↑ (reflex)',
      contractility: '↔',
      SVR: '↓↓↓',
      PVR: '↓↓',
      preload: '↓↓'
    },
    dosing: {
      infusion: '0.3-10 mcg/kg/min (max 10 mcg/kg/min)',
      units: 'mcg/kg/min'
    },
    concentration: {
      standard: '100 mcg/mL (50 mg in 500 mL)'
    },
    onset: 'Immediate (30 seconds)',
    duration: '1-2 minutes',
    metabolism: 'RBCs → cyanide → liver → thiocyanate',
    uses: [
      'Hypertensive emergency',
      'Acute aortic dissection',
      'Controlled hypotension',
      'Afterload reduction in severe MR/AR'
    ],
    considerations: [
      'Balanced arterial and venous dilator',
      'FASTEST onset of any antihypertensive',
      'Risk of cyanide toxicity (>3 mcg/kg/min for >24-48hr)',
      'Risk of thiocyanate toxicity in renal failure',
      'Light-sensitive - wrap in foil',
      'Can cause coronary steal',
      'Increases ICP',
      'Generally replaced by nicardipine'
    ],
    bestFor: 'Immediate BP control (but nicardipine preferred)'
  },
  {
    name: 'Hydralazine',
    proprietaryName: 'Apresoline',
    class: 'vasodilator',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'Direct smooth muscle relaxant': '+++'
    },
    hemodynamics: {
      HR: '↑↑ (reflex)',
      contractility: '↑ (reflex)',
      SVR: '↓↓',
      PVR: '↓',
      preload: '↔'
    },
    dosing: {
      bolus: '5-20 mg IV q20-30min',
      units: 'mg'
    },
    concentration: {
      standard: 'Supplied as 20 mg/mL vials'
    },
    onset: '10-20 minutes',
    duration: '2-6 hours',
    metabolism: 'Hepatic (acetylation)',
    uses: [
      'Preeclampsia/eclampsia',
      'Hypertensive urgency',
      'Afterload reduction in heart failure'
    ],
    considerations: [
      'Direct arteriodilator',
      'Unpredictable onset and duration',
      'Profound reflex tachycardia',
      'Can precipitate angina',
      'SLE-like syndrome with chronic use',
      'Safe in pregnancy (but labetalol preferred)',
      'Variable response based on acetylator status'
    ],
    bestFor: 'Preeclampsia (though labetalol often preferred)'
  },
  {
    name: 'Esmolol',
    proprietaryName: 'Brevibloc',
    class: 'vasodilator',
    categories: ['cardiovascular'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'β1': '+++'
    },
    hemodynamics: {
      HR: '↓↓',
      contractility: '↓',
      SVR: '↔',
      PVR: '↔',
      preload: '↔'
    },
    dosing: {
      loading: '500 mcg/kg over 1 min',
      infusion: '50-300 mcg/kg/min',
      units: 'mcg/kg/min'
    },
    concentration: {
      standard: '10 mg/mL (2.5 g in 250 mL)'
    },
    onset: '1-2 minutes',
    duration: '10-20 minutes',
    metabolism: 'RBC esterases',
    uses: [
      'Supraventricular tachycardia',
      'Atrial fibrillation/flutter (rate control)',
      'Perioperative hypertension with tachycardia',
      'Thyrotoxicosis',
      'Aortic dissection'
    ],
    considerations: [
      'Ultra-short-acting β-blocker',
      'Rapidly titratable',
      'Can worsen heart failure',
      'Bronchospasm in asthmatics',
      'Masks hypoglycemia symptoms',
      'Safe to use in acute MI',
      'Metabolized independent of liver/kidney'
    ],
    bestFor: 'Acute rate control'
  },

  // ========== ANTIARRHYTHMICS ==========
  {
    name: 'Amiodarone',
    proprietaryName: 'Cordarone',
    class: 'antiarrhythmic',
    categories: ['cardiovascular', 'emergency'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'Class III': '+++'
    },
    hemodynamics: {
      HR: '↓',
      contractility: '↓',
      SVR: '↓',
      PVR: '↔',
      preload: '↔'
    },
    dosing: {
      vfib: '300 mg IV bolus, then 150 mg if needed',
      afib: '150 mg over 10 min, then 1 mg/min × 6hr, then 0.5 mg/min',
      units: 'mg'
    },
    concentration: {
      standard: 'Premixed 1.5 mg/mL or 1.8 mg/mL'
    },
    onset: '15-30 minutes IV',
    duration: 'Days to weeks (extremely long half-life)',
    metabolism: 'Hepatic (CYP3A4)',
    uses: [
      'VF/pulseless VT (ACLS)',
      'Atrial fibrillation (rate and rhythm control)',
      'Ventricular tachycardia',
      'Refractory arrhythmias'
    ],
    considerations: [
      'Broad-spectrum antiarrhythmic',
      'Can cause hypotension (due to Polysorbate vehicle)',
      'Multiple organ toxicities with chronic use',
      'Prolongs QT interval',
      'Photosensitivity',
      'Thyroid dysfunction',
      'Pulmonary fibrosis',
      'Requires central line for prolonged infusion'
    ],
    bestFor: 'Refractory VT/VF or AF',
    contexts: {
      cardiovascular: {
        subtitle: 'Broad-spectrum antiarrhythmic',
        dosing: '150 mg over 10 min',
        uses: 'AF, VT, refractory arrhythmias'
      },
      emergency: {
        subtitle: 'ACLS drug for VF/pulseless VT',
        dosing: '300 mg IV bolus (first dose)',
        uses: 'Shock-refractory VF/VT'
      }
    }
  },
  {
    name: 'Adenosine',
    proprietaryName: 'Adenocard',
    class: 'antiarrhythmic',
    categories: ['cardiovascular', 'emergency'],
    primaryCategory: 'cardiovascular',
    receptors: {
      'Adenosine receptor': '+++'
    },
    hemodynamics: {
      HR: '↓↓↓ (transient)',
      contractility: '↓ (transient)',
      SVR: '↔',
      PVR: '↑ (transient)',
      preload: '↔'
    },
    dosing: {
      initial: '6 mg rapid IV push',
      repeat: '12 mg if no response',
      units: 'mg'
    },
    concentration: {
      standard: '3 mg/mL (supplied as 2 mL vials)'
    },
    onset: '10-20 seconds',
    duration: '<10 seconds',
    metabolism: 'Cellular uptake by RBCs and endothelium',
    uses: [
      'SVT termination (first-line)',
      'Diagnostic tool for wide-complex tachycardia'
    ],
    considerations: [
      'Extremely short half-life',
      'MUST give rapid IV push followed by saline flush',
      'Transient asystole common (warn patient)',
      'Sense of "impending doom" common',
      'Bronchospasm (avoid in asthma)',
      'Contraindicated in WPW with AF',
      'Dipyridamole potentiates effect',
      'Theophylline/caffeine antagonize effect',
      'Higher doses needed via peripheral IV'
    ],
    bestFor: 'SVT termination'
  }
];

// Export organized by class for easy filtering
export const vasopressors = cvMedications.filter(med => med.class === 'vasopressor');
export const inotropes = cvMedications.filter(med => med.class === 'inotrope');
export const vasodilators = cvMedications.filter(med => med.class === 'vasodilator');
export const antiarrhythmics = cvMedications.filter(med => med.class === 'antiarrhythmic');
